import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlaneDeparture, faPlaneArrival, faCar, faHotel, faMapMarkerAlt, faTasks, faStickyNote, 
    faSave, faArrowRight, faCalendarDay, faClock, faBed, faMountainSun, faRoute, faStar, faPlane, faPen,
    faChevronDown, faChevronUp, faCheck, faTimes
} from '@fortawesome/free-solid-svg-icons';
import './PackageItineraray.css';
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import { toast } from "react-toastify";

// --- Reusable Modal Component ---
const SelectionModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="itinerary-modal-backdrop" onClick={onClose}>
            <div className="itinerary-modal-content" onClick={e => e.stopPropagation()}>
                <div className="itinerary-modal-header">
                    <h5>{title}</h5>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="itinerary-modal-body">{children}</div>
            </div>
        </div>
    );
};


// --- Child Components ---

const FlightDetailCard = ({ flight, onChangeClick }) => {
    if (!flight) return <div className="detail-card placeholder-card">No Flight Data Available</div>;

    if (flight.dcFlightLegs?.[0]?.dcDisplayLegs?.length > 0) {
        const leg = flight.dcFlightLegs[0].dcDisplayLegs[0];
         const price = flight.dcFlightLegs[0].dcPriceDetails?.[0]?.totalPrice;
        const departureTime = moment(leg.startSegment.departureDateTime).format("HH:mm");
        const arrivalTime = moment(leg.endSegment.arrivalDateTime).format("HH:mm");
        const formatDuration = (min) => `${Math.floor(min / 60)}h ${min % 60}m`;
        const airlineLogo = `/assets/images/airlinelogos/${leg.airlineData.airlineCode}.png`;
        
        return (
            <div className="detail-card flight-detail-card">
                 <div className="detail-card-body">
                    <div className="flight-content">
                        <div className="airline-info">
                            <div className="airline-logo">
                                <img src={airlineLogo} alt={leg.airlineData.airline} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                <div className="airline-fallback">{leg.airlineData.airlineCode}</div>
                            </div>
                            <div className="airline-details">
                                <div className="airline-name">{leg.airlineData.airline}</div>
                                <div className="flight-number">{leg.airlineData.airlineCode} {leg.airlineData.flightNumber}</div>
                            </div>
                        </div>
                        <div className="flight-timeline-section">
                            <div className="flight-endpoint departure">
                                <div className="time">{departureTime}</div>
                                <div className="airport-code">{leg.startSegment.airportCode}</div>
                                <div className="city-name">{leg.startSegment.city}</div>
                            </div>
                            <div className="journey-info">
                                <div className="duration"><FontAwesomeIcon icon={faClock} /> {formatDuration(leg.totalDuration)}</div>
                                <div className="arrow-line"><div className="line"></div><FontAwesomeIcon icon={faPlane} className="plane-icon" /></div>
                                <div className="stops">{leg.stops === 0 ? 'Non-Stop' : `${leg.stops} Stop(s)`}</div>
                            </div>
                            <div className="flight-endpoint arrival">
                                <div className="time">{arrivalTime}</div>
                                <div className="airport-code">{leg.endSegment.airportCode}</div>
                                <div className="city-name">{leg.endSegment.city}</div>
                            </div>
                            {price && <div className="price-display">INR{Math.round(price).toLocaleString('en-IN')}</div>}
                            {onChangeClick && (
                                <div className="change-button-container">
                                    <button className="change-btn" onClick={onChangeClick}><FontAwesomeIcon icon={faPen} /> Change</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
    
    return <div className="detail-card placeholder-card">Flight Data Incomplete</div>;
};

const FlightSelectionCard = ({ flight, onSelect, isSelected }) => {
    // --- CORRECTED: The component now checks the correct nested path for detailed data ---
    if (flight.dcFlightLegs?.[0]?.dcDisplayLegs?.length > 0) {
        
        const detailedLegData = flight.dcFlightLegs[0]; // The object containing dcDisplayLegs and dcPriceDetails
        const displayLeg = detailedLegData.dcDisplayLegs[0]; // The object for display purposes

        const departureTime = moment(displayLeg.startSegment.departureDateTime).format("HH:mm");
        const arrivalTime = moment(displayLeg.endSegment.arrivalDateTime).format("HH:mm");
        const formatDuration = (min) => `${Math.floor(min / 60)}h ${min % 60}m`;
        const airlineLogo = `/assets/images/airlinelogos/${displayLeg.airlineData.airlineCode}.png`;
        
        // <<< FIX: Access price from the correct nested location
        const price = detailedLegData.dcPriceDetails?.[0]?.totalPrice;

        return (
            <div className={`selection-card flight-selection-card ${isSelected ? 'is-selected' : ''}`}>
                <div className="selection-card-body detailed-view">
                    <div className="airline-info">
                        <div className="airline-logo">
                            <img src={airlineLogo} alt={displayLeg.airlineData.airline} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                            <div className="airline-fallback">{displayLeg.airlineData.airlineCode}</div>
                        </div>
                        <div className="airline-details">
                            <div className="airline-name">{displayLeg.airlineData.airline}</div>
                            <div className="flight-number">{displayLeg.airlineData.airlineCode} {displayLeg.airlineData.flightNumber}</div>
                        </div>
                    </div>
                    <div className="flight-timeline-section">
                        <div className="flight-endpoint departure">
                            <div className="time">{departureTime}</div>
                            <div className="airport-code">{displayLeg.startSegment.airportCode}</div>
                        </div>
                        <div className="journey-info">
                            <div className="duration">{formatDuration(displayLeg.totalDuration)}</div>
                            <div className="arrow-line"><div className="line"></div></div>
                            <div className="stops">{displayLeg.stops === 0 ? 'Non-Stop' : `${displayLeg.stops} Stop(s)`}</div>
                        </div>
                        <div className="flight-endpoint arrival">
                            <div className="time">{arrivalTime}</div>
                            <div className="airport-code">{displayLeg.endSegment.airportCode}</div>
                        </div>
                    </div>
                    <div className="select-button-container">
                        {price && <div className="price-display">INR{Math.round(price).toLocaleString('en-IN')}</div>}
                        {isSelected ? (
                            <button className="select-btn selected-btn" disabled>Selected</button>
                        ) : (
                            <button className="select-btn" onClick={() => onSelect(flight)}>Select</button>
                        )}
                    </div>
                </div>
            </div>
        );
    }
    return null; // Don't render if data is invalid
};

const TransferDetailCard = ({ transfer }) => {
  if (!transfer) return <div className="detail-card placeholder-card">No Transfer Data Available</div>;
  return (
    <div className="detail-card transfer-detail-card">
      <div className="detail-card-body">
        <div className="transfer-icon"><FontAwesomeIcon icon={faCar} /></div>
        <div className="transfer-info">
          <span className="vehicle-type">{transfer.vehicleType}</span>
          <span className="location">{transfer.from}</span>
          <FontAwesomeIcon icon={faArrowRight} className="transfer-arrow" />
          <span className="location">{transfer.to}</span>
        </div>
      </div>
    </div>
  );
};

const HotelDetailCard = ({ hotel, onChangeClick }) => {
  if (!hotel) return null;
  return (
   <div className="detail-card hotel-detail-card">
      <div className="detail-card-body">
        <div className="hotel-info">
          <div className="hotel-name"><FontAwesomeIcon icon={faHotel} /> {hotel.hotelName}</div>
          <div className="hotel-location"><FontAwesomeIcon icon={faMapMarkerAlt} /> {hotel.cityName}, {hotel.countryName}</div>
          <div className="hotel-rating"><FontAwesomeIcon icon={faStar} /> {hotel.rating} Star</div>
        </div>
        {onChangeClick && (
          <button className="change-btn" onClick={onChangeClick}><FontAwesomeIcon icon={faPen} /> Change</button>
        )}
      </div>
    </div>
  );
};

const HotelSelectionCard = ({ hotel, onSelect, isSelected }) => {
  if (!hotel) return null;
  return (
    <div className={`selection-card hotel-selection-card ${isSelected ? 'is-selected' : ''}`}>
      <div className="selection-card-body">
        <div className="hotel-info">
          <div className="hotel-name"><FontAwesomeIcon icon={faHotel} /> {hotel.hotelName}</div>
          <div className="hotel-location"><FontAwesomeIcon icon={faMapMarkerAlt} /> {hotel.cityName}, {hotel.countryName}</div>
          <div className="hotel-rating"><FontAwesomeIcon icon={faStar} /> {hotel.rating} Star</div>
        </div>
        <div className="select-button-container">
            {isSelected ? (
                <button className="select-btn selected-btn" disabled>Selected</button>
            ) : (
                <button className="select-btn" onClick={() => onSelect(hotel)}>Select</button>
            )}
        </div>
      </div>
    </div>
  );
};

const DayCard = ({ day, isOpen, onToggle, onChangeHotelClick }) => {
    const [activeTab, setActiveTab] = useState('activities');
    const hasHotel = day.hotel && day.hotel.length > 0;
    const hasActivities = day.activities && day.activities.length > 0;
    const hasTransfer = !!day.transfer;

    useEffect(() => {
        if (activeTab === 'hotel' && !hasHotel) setActiveTab('activities');
        if (activeTab === 'transfer' && !hasTransfer) setActiveTab('activities');
    }, [day, activeTab, hasHotel, hasTransfer]);

    return (
        <div className={`day-card ${isOpen ? 'is-open' : ''}`}>
            <div className="day-card-header" onClick={onToggle}>
                <h6>Day {day.dayNumber}: {day.title || day.area}</h6>
                <div className='d-flex align-items-center'>
                    <div className="date-display"><FontAwesomeIcon icon={faCalendarDay} /> {moment(day.date).format("ddd, MMM D")}</div>
                    <FontAwesomeIcon className="collapse-icon" icon={isOpen ? faChevronUp : faChevronDown} />
                </div>
            </div>
            {isOpen && (
                <div className="day-card-body">
                    <p className="day-summary">{day.summary}</p>
                    <div className="day-card-tabs">
                       {hasHotel && <button onClick={() => setActiveTab('hotel')} className={activeTab === 'hotel' ? 'active' : ''}><FontAwesomeIcon icon={faBed} /> Hotel</button>}
                       {hasActivities && <button onClick={() => setActiveTab('activities')} className={activeTab === 'activities' ? 'active' : ''}><FontAwesomeIcon icon={faTasks} /> Activities</button>}
                       {hasTransfer && <button onClick={() => setActiveTab('transfer')} className={activeTab === 'transfer' ? 'active' : ''}><FontAwesomeIcon icon={faRoute} /> Transfer</button>}
                    </div>
                    <div className="day-card-content-panel">
                      {activeTab === 'hotel' && hasHotel && <HotelDetailCard hotel={day.hotel[0]} onChangeClick={onChangeHotelClick} />}
                      {activeTab === 'activities' && hasActivities && <ul className="activities-list">{day.activities.map((act, i) => <li key={i}>{act}</li>)}</ul>}
                      {activeTab === 'transfer' && hasTransfer && <TransferDetailCard transfer={day.transfer} />}
                    </div>
                    {day.notes && (<p className="day-notes"><FontAwesomeIcon icon={faStickyNote} /> <strong>Note:</strong> {day.notes}</p>)}
                </div>
            )}
        </div>
    );
};


// --- Main Component ---
const AISupplierPackageItinerary = ({ initialData, onContinueClick }) => {
    const { user } = useAuth();
    const navigate = useNavigate(); 
    const [btnLoading, setBtnLoading] = useState(false);
    const [dailyData, setDailyData] = useState([]);
    const [selectedOnwardFlight, setSelectedOnwardFlight] = useState(null);
    const [selectedReturnFlight, setSelectedReturnFlight] = useState(null);
    const [allOnwardFlights, setAllOnwardFlights] = useState([]);
    const [allReturnFlights, setAllReturnFlights] = useState([]);
    const [allHotels, setAllHotels] = useState([]);
    const [isFlightModalOpen, setFlightModalOpen] = useState(false);
    const [flightModalConfig, setFlightModalConfig] = useState({ type: null, options: [] });
    const [isHotelModalOpen, setHotelModalOpen] = useState(false);
    const [hotelModalConfig, setHotelModalConfig] = useState({ dayIndex: null });
    const [openDay, setOpenDay] = useState(1);

    const toggleDayCard = (dayNum) => setOpenDay(openDay === dayNum ? null : dayNum);

  // Replace your entire useEffect hook with this corrected version
// Replace your entire useEffect hook with this improved version
useEffect(() => {
    if (initialData && initialData.days && initialData.days.length > 0) {
        const dataCopy = JSON.parse(JSON.stringify(initialData));

        const transformFlights = (flightArray) => {
            if (!flightArray || flightArray.length === 0) return [];
            const allLegs = flightArray[0]?.dcFlightLegs || [];
            return allLegs.map(leg => ({
                destinationCount: flightArray[0].destinationCount,
                journeyType: flightArray[0].journeyType,
                locationFrom: flightArray[0].locationFrom,
                locationTo: flightArray[0].locationTo,
                dcFlightLegs: [leg]
            }));
        };

        // --- NEW & ROBUST FLIGHT FINDING LOGIC ---
        // Find the first day that has onward flights
        const firstDayWithFlights = dataCopy.days.find(day => day.flights && day.flights.length > 0 && day.flights[0].journeyType === 'ONWARD');
        const onwardFlights = transformFlights(firstDayWithFlights?.flights);

        // Find the last day that has return flights (or any flights as a fallback)
        const lastDayWithFlights = [...dataCopy.days].reverse().find(day => day.flights && day.flights.length > 0);
        const returnFlights = transformFlights(lastDayWithFlights?.flights);
        // --- END OF NEW LOGIC ---

        setAllOnwardFlights(onwardFlights);
        setAllReturnFlights(returnFlights);
        
        setSelectedOnwardFlight(onwardFlights.length > 0 ? onwardFlights[0] : null);
        setSelectedReturnFlight(returnFlights.length > 0 ? returnFlights[0] : null);

        const uniqueHotels = [];
        const hotelIds = new Set();
        dataCopy.days.forEach(day => {
            if (day.hotel && day.hotel.length > 0 && !hotelIds.has(day.hotel[0].hotelId)) {
                uniqueHotels.push(day.hotel[0]);
                hotelIds.add(day.hotel[0].hotelId);
            }
        });
        setAllHotels(uniqueHotels);
        setDailyData(dataCopy.days);
    }
}, [initialData]);

    const handleOpenFlightModal = (type) => {
        setFlightModalConfig({
            type,
            options: type === 'onward' ? allOnwardFlights : allReturnFlights,
            title: type === 'onward' ? `Select Departure Flight (${selectedOnwardFlight?.locationFrom} to ${selectedOnwardFlight?.locationTo})` : `Select Return Flight (${selectedReturnFlight?.locationFrom} to ${selectedReturnFlight?.locationTo})`
        });
        setFlightModalOpen(true);
    };

    const handleSelectFlight = (flight) => {
        if (flightModalConfig.type === 'onward') {
            setSelectedOnwardFlight(flight);
        } else {
            setSelectedReturnFlight(flight);
        }
        setFlightModalOpen(false);
    };

    const handleOpenHotelModal = (dayIndex) => {
        setHotelModalConfig({ dayIndex });
        setHotelModalOpen(true);
    };

    const handleSelectHotel = (newHotel) => {
        const { dayIndex } = hotelModalConfig;
        if (dayIndex === null) return;
        const updatedDailyData = [...dailyData];
        const oldHotelId = updatedDailyData[dayIndex].hotel[0]?.hotelId;
        if (!oldHotelId) return;

        for (let i = 0; i < updatedDailyData.length; i++) {
            if (updatedDailyData[i].hotel?.[0]?.hotelId === oldHotelId) {
                updatedDailyData[i].hotel[0] = { ...newHotel };
            }
        }
        setDailyData(updatedDailyData);
        setHotelModalOpen(false);
    };
    
    const inclusions = [ "Return economy class airfare", "Accommodation in specified hotels", "Daily breakfast at the hotel", "All transfers and sightseeing by private vehicle", "All applicable taxes" ];
    const exclusions = [ "Visa fees", "Travel insurance", "Lunch and dinner unless specified", "Any personal expenses", "Tips and porter charges" ];
    const terms = [ { title: "Pricing", description: "Package rates are subject to change without prior notice." }, { title: "Availability", description: "Booking confirmation is subject to availability of flights and hotels." }, { title: "Cancellation", description: "Cancellation charges apply as per the policy." } ];

    const handleSaveAllSuggestions = async () => {
        if (!initialData || !dailyData || dailyData.length === 0) {
            alert("No itinerary data to save.");
            return;
        }

        setBtnLoading(true);

        try {
            const packagePayload = {
                packageName: (initialData.packageName || "Holiday Package").substring(0, 49),
                duration: initialData.duration || "4 Days",
                startedDate: initialData.startedDate,
                destinationsCover: initialData.destinationsCover,
                noOfPersons: initialData.noOfPersons || 2,
                description: initialData.description || "AI Generated Package", 
                price: initialData.price || 10000,
                imageName: "default-package.jpg",
                type: "Holiday",
                companyId: user?.cid,
                userId: user?.id,
                destinationId: "cf4cce04-b9fc-42a6-b545-2fac0a52e094", 
                travelTypeId: "253f65cb-9577-4cdd-9dba-7284732fbf2d",
                Currency    : user?.currency || "INR",
                status: "InActive",
            };

            const packageFormData = new FormData();
            for (const key in packagePayload) packageFormData.append(key, packagePayload[key] ?? "");

            const packageResponse = await apiService.post(config.CREATEPACKAGE, packageFormData);
            const newPackageId = packageResponse.packageId;

            if (!newPackageId) throw new Error("Failed to create package. No packageId returned.");

            const promises = [];

            dailyData.forEach(day => {
                promises.push(apiService.post(config.CREATEPACKAGEITINERARY, { packageId: newPackageId, title: day.title || day.area, date: day.date, location: day.area, description: day.summary, meals: day.meals || "Not Specified", note: day.notes, userId: user?.id, }));
                if (day.hotel && day.hotel.length > 0) promises.push(apiService.post(config.CREATEPACKAGEHOTEL, { packageId: newPackageId, checkInDate: day.date, checkOutDate: moment(day.date).add(day.nights || 1, 'days').format("YYYY-MM-DD"), city: day.hotel[0].cityName, nights: day.nights || 1, hotelName: day.hotel[0].hotelName, roomType: day.roomType || "Standard", numberOfRooms: day.numberOfRooms || 1, userId: user?.id, }));
                if (day.tours && day.tours.length > 0) day.tours.forEach(tour => promises.push(apiService.post(config.CREATEPACKAGESIGHT, { packageId: newPackageId, date: day.date, destination: day.area, sightseeingName: tour.title, description: tour.description, userId: user?.id, })));
                if (day.transfer && day.transfer.from) promises.push(apiService.post(config.CREATEPACKAGETRANSFER, { packageId: newPackageId, date: day.date, pickupTime: day.transfer.pickupTime || "N/A", fromLocation: day.transfer.from, toLocation: day.transfer.to, vehicleDetails: day.transfer.vehicleType, userId: user?.id, }));
            });

            // Corrected Flight Saving Logic
            const saveFlight = (flight) => {
                if (flight?.dcFlightLegs?.[0]?.dcDisplayLegs?.[0]) {
                    const leg = flight.dcFlightLegs[0].dcDisplayLegs[0];
                    const priceDetails = flight.dcFlightLegs[0].dcPriceDetails?.[0];
                    promises.push(apiService.post(config.CREATEPACKAGEFLIGHT, {
                        packageId: newPackageId,
                        airline: leg.airlineData.airline,
                        flightNumber: leg.airlineData.flightNumber,
                        departureCity: leg.startSegment.city,
                        arrivalCity: leg.endSegment.city,
                        departureDateTime: leg.startSegment.departureDateTime,
                        arrivalDateTime: leg.endSegment.arrivalDateTime,
                        cabinClass: priceDetails?.dcAdults?.cabinClass || "Economy",
                        userId: user?.id,
                    }));
                }
            };
            saveFlight(selectedOnwardFlight);
            saveFlight(selectedReturnFlight);


            inclusions.forEach(item => promises.push(apiService.post(config.CREATEPACKAGEINCLUDE, { packageId: newPackageId, description: item, userId: user?.id })));
            exclusions.forEach(item => promises.push(apiService.post(config.CREATEPACKAGEEXCLUDE, { packageId: newPackageId, description: item, userId: user?.id })));
            terms.forEach(term => promises.push(apiService.post(config.CREATEPACKAGETERMS, { packageId: newPackageId, title: term.title, description: term.description, userId: user?.id })));

            await Promise.all(promises);
            toast.success("Package and itinerary saved successfully!");
            navigate(`/package/${newPackageId}`);
        } catch (error) {
            console.error("Error saving the full package:", error);
            toast.error(`An error occurred while saving: ${error.message || 'Please check the console for details.'}`);
        } finally {
            setBtnLoading(false);
        }
    };

    const handleContinue = () => onContinueClick(2, { ...initialData, days: dailyData, selectedOnwardFlight, selectedReturnFlight });
    
    if (dailyData.length === 0) return <div className="text-center p-5"><h5>No Itinerary Data Found.</h5></div>;

    return (
        <>
            <div className="ai-suggestions-container mb-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                     <h5 className="mb-0">AI Suggested Itinerary</h5>
                     <div className="d-flex gap-2">
                        <button className="btn btn-success" onClick={handleSaveAllSuggestions} disabled={btnLoading}>
                             <FontAwesomeIcon icon={faSave} /> {btnLoading ? 'Saving...' : 'Save All Suggestions'}
                        </button>
                        <button className="btn btn-primary" onClick={handleContinue}>Continue to Share Template</button>
                     </div>
                 </div>

                <div className="itinerary-wrapper">
                    {/* Departure Segment */}
                    <div className="journey-segment departure-segment">
                        <div className="segment-marker"><FontAwesomeIcon icon={faPlaneDeparture} /></div>
                        <div className="segment-content">
                            <h3 className="segment-title">Departure Journey</h3>
                            <p className="segment-subtitle">From {selectedOnwardFlight?.locationFrom} to {selectedOnwardFlight?.locationTo}</p>
                            <FlightDetailCard flight={selectedOnwardFlight} onChangeClick={() => handleOpenFlightModal('onward')} />
                            <h5 className="sub-heading">Arrival Transfer</h5>
                            <TransferDetailCard transfer={dailyData[0]?.transfer} />
                        </div>
                    </div>
                    {/* Daily Itinerary Segment */}
                    <div className="journey-segment daily-itinerary-segment">
                        <div className="segment-marker"><FontAwesomeIcon icon={faCalendarDay} /></div>
                        <div className="segment-content">
                            <h3 className="segment-title">Daily Itinerary</h3>
                            {dailyData.map((day, index) => <DayCard key={day.dayNumber} day={day} isOpen={openDay === day.dayNumber} onToggle={() => toggleDayCard(day.dayNumber)} onChangeHotelClick={() => handleOpenHotelModal(index)} />)}
                        </div>
                    </div>
                    {/* Return Segment */}
                    {(dailyData[dailyData.length - 1]?.transfer || selectedReturnFlight) && (
                        <div className="journey-segment return-segment">
                            <div className="segment-marker"><FontAwesomeIcon icon={faPlaneArrival} /></div>
                            <div className="segment-content">
                                <h3 className="segment-title">Return Journey</h3>
                                {dailyData[dailyData.length - 1]?.transfer && <> <h5 className="sub-heading">Departure Transfer</h5> <TransferDetailCard transfer={dailyData[dailyData.length - 1]?.transfer} /> </>}
                                {selectedReturnFlight && <FlightDetailCard flight={selectedReturnFlight} onChangeClick={() => handleOpenFlightModal('return')} />}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <SelectionModal isOpen={isFlightModalOpen} onClose={() => setFlightModalOpen(false)} title={flightModalConfig.title}>
                <div className="selection-list">
                    {flightModalConfig.options.length > 0 ? flightModalConfig.options.map(flight => (
                        <FlightSelectionCard key={flight.dcFlightLegs[0].productKey} flight={flight} onSelect={handleSelectFlight} isSelected={flight.dcFlightLegs[0].productKey === (flightModalConfig.type === 'onward' ? selectedOnwardFlight?.dcFlightLegs[0].productKey : selectedReturnFlight?.dcFlightLegs[0].productKey)} />
                    )) : <div className="text-center p-3">No other flights available.</div>}
                </div>
            </SelectionModal>

            <SelectionModal isOpen={isHotelModalOpen} onClose={() => setHotelModalOpen(false)} title="Select a Hotel">
                <div className="selection-list">
                    {allHotels.map(hotel => <HotelSelectionCard key={hotel.hotelId} hotel={hotel} onSelect={handleSelectHotel} isSelected={hotel.hotelId === dailyData[hotelModalConfig.dayIndex]?.hotel[0]?.hotelId} />)}
                </div>
            </SelectionModal>

            {/* Additional Details Section */}
            <div className="additional-details-container" style={{ marginTop: '2rem' }}>
                <div className="details-section-card"><h3>Inclusions</h3><ul className="info-list include-list">{inclusions.map((item, index) => (<li key={index}><FontAwesomeIcon icon={faCheck} className="list-icon" />{item}</li>))}</ul></div>
                <div className="details-section-card"><h3>Exclusions</h3><ul className="info-list exclude-list">{exclusions.map((item, index) => (<li key={index}><FontAwesomeIcon icon={faTimes} className="list-icon" />{item}</li>))}</ul></div>
                <div className="details-section-card"><h3>Terms & Conditions</h3><ol className="info-list terms-list">{terms.map((item, index) => (<li key={index}><strong>{item.title}:</strong> {item.description}</li>))}</ol></div>
            </div>
        </>
    );
};

export default AISupplierPackageItinerary;